import {
  chunkContentRange,
  chunkEnd,
  nextOffsetFromRange,
  RESUMABLE_CHUNK_BYTES,
  statusQueryContentRange,
} from '../../lib/portalAssets/uploadPlan';

// THE BROWSER SIDE OF A GCS RESUMABLE UPLOAD (spec §4.3).
//
// ⚠️ A NEW HELPER, deliberately. `putFileWithProgress` (src/modules/metaAds/api.ts)
// is a single XHR PUT serving the Meta ad flow with a different cap, and turning
// it into a resumable one would change that flow. A 2 GB phone video over 4G is
// exactly the upload that gets interrupted, so this one resumes.
//
// The protocol: POST the signed URL with the server's `requiredHeaders` (which
// carry `x-goog-resumable: start`) and an empty body → 201 with the SESSION URI
// in `Location`. Then PUT 8 MiB chunks with `Content-Range`; GCS answers 308
// with `Range: bytes=0-N` while incomplete, and 200/201 on the last chunk. After
// a dropped connection, a zero-length PUT with `Content-Range: bytes */total`
// asks how much it has, and the upload continues from there.
//
// ⚠️ `fetch` treats 308 as a redirect status, but a 308 with NO `Location` is
// returned as-is (the fetch spec only follows a redirect that names a target),
// which is what GCS sends. `redirect: 'manual'` would be WRONG here: across
// origins it yields an opaque response whose `Range` header cannot be read.
//
// ⚠️ The transport is INJECTED so the whole state machine is tested in node
// with a fake GCS — no DOM, no network.

export type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

export class ResumableUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ResumableUploadError';
  }
}

const TRANSIENT = new Set([408, 429, 500, 502, 503, 504]);
const DONE = new Set([200, 201]);

export type ResumableInput = {
  file: Blob;
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
  onProgress?: (sentBytes: number, totalBytes: number) => void;
  fetchImpl?: FetchLike;
  sleep?: (ms: number) => Promise<void>;
  chunkBytes?: number;
  /** Consecutive failures tolerated before giving up. */
  maxRetries?: number;
};

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function uploadResumable(input: ResumableInput): Promise<void> {
  const doFetch: FetchLike = input.fetchImpl ?? ((url, init) => fetch(url, init));
  const sleep = input.sleep ?? defaultSleep;
  const chunkBytes = input.chunkBytes ?? RESUMABLE_CHUNK_BYTES;
  const maxRetries = input.maxRetries ?? 5;
  const total = input.file.size;

  const start = await doFetch(input.uploadUrl, { method: 'POST', headers: input.requiredHeaders, body: '' });
  if (!DONE.has(start.status)) {
    throw new ResumableUploadError(`The upload couldn't start (HTTP ${start.status}).`);
  }
  const session = start.headers.get('Location');
  if (!session) {
    // ⚠️ Most likely CORS: the bucket must EXPOSE `Location` (spec §11 step 2).
    throw new ResumableUploadError("The upload couldn't start: the storage service didn't return a session.");
  }

  let offset = 0;
  let failures = 0;
  input.onProgress?.(0, total);

  while (offset < total) {
    const end = chunkEnd(offset, total, chunkBytes);
    let res: Response | null = null;
    try {
      res = await doFetch(session, {
        method: 'PUT',
        headers: { 'Content-Range': chunkContentRange(offset, end, total) },
        body: input.file.slice(offset, end),
      });
    } catch {
      res = null; // a dropped connection — ask the session where it got to
    }

    if (res && DONE.has(res.status)) {
      offset = total;
      break;
    }
    if (res && res.status === 308) {
      const next = nextOffsetFromRange(res.headers.get('Range'));
      if (next > offset) failures = 0;
      else if (++failures > maxRetries) throw new ResumableUploadError('The upload stopped making progress.');
      offset = next;
      input.onProgress?.(offset, total);
      continue;
    }
    if (res && (res.status === 404 || res.status === 410)) {
      throw new ResumableUploadError('The upload session expired. Choose the file again to retry.');
    }
    if (res && !TRANSIENT.has(res.status)) {
      throw new ResumableUploadError(`The upload was refused (HTTP ${res.status}).`);
    }

    // Transient failure or dropped connection: back off, then re-sync.
    if (++failures > maxRetries) {
      throw new ResumableUploadError('The connection kept dropping. Check your signal and try again.');
    }
    await sleep(Math.min(30_000, 1000 * 2 ** (failures - 1)));
    const synced = await queryOffset(doFetch, session, total);
    if (synced === 'done') {
      offset = total;
      break;
    }
    if (synced !== null) {
      offset = synced;
      input.onProgress?.(offset, total);
    }
  }
  input.onProgress?.(total, total);
}

/** The status query. `null` = it failed too; the loop retries. */
async function queryOffset(doFetch: FetchLike, session: string, total: number): Promise<number | 'done' | null> {
  try {
    const res = await doFetch(session, {
      method: 'PUT',
      headers: { 'Content-Range': statusQueryContentRange(total) },
      body: '',
    });
    if (DONE.has(res.status)) return 'done';
    if (res.status === 308) return nextOffsetFromRange(res.headers.get('Range'));
    if (res.status === 404 || res.status === 410) {
      throw new ResumableUploadError('The upload session expired. Choose the file again to retry.');
    }
    return null;
  } catch (err) {
    if (err instanceof ResumableUploadError) throw err;
    return null;
  }
}
